import React, { useCallback, useMemo, useState } from 'react';
import DateTimePicker from '../../components/DateTimePicker';
import SingleSelect from '../../components/SingleSelect';
import { TanTable } from '../../components/TanTable';
import { getDispatcherStreetTurns, type StreetTurnCountsResponse, type StreetTurnCountsRow, type StreetTurnGroupBy } from '../../services/dispatcherStreetTurns.service';
import { calendarDate, dateParam, reportError, REPORT_BUTTON } from '../accounting/reports/reportUtils';
import { streetTurnCountsColumns, streetTurnCountsExportFooter } from './streetTurnCountsColumns';

const GROUPS = [
  { value: 'DAY', label: 'Daily' }, { value: 'WEEK', label: 'Weekly' },
  { value: 'MONTH', label: 'Monthly' }, { value: 'YEAR', label: 'Yearly' },
];
const PRESETS = ['Today', 'This Week', 'This Month', 'This Year'] as const;
const centralToday = () => new Date(`${new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Chicago' }).format(new Date())}T00:00:00`);

export default function DispatcherStreetTurns() {
  const [start, setStart] = useState<Date | null>(() => { const day = centralToday(); day.setDate(1); return day; });
  const [end, setEnd] = useState<Date | null>(centralToday);
  const [groupBy, setGroupBy] = useState<StreetTurnGroupBy>('DAY');
  const [result, setResult] = useState<StreetTurnCountsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const columns = useMemo(() => streetTurnCountsColumns(result?.periods ?? [], result?.groupBy ?? 'DAY'), [result]);
  const exportFooter = useCallback((rows: StreetTurnCountsRow[]) => streetTurnCountsExportFooter(result?.periods ?? [], rows), [result]);

  const preset = (label: typeof PRESETS[number]) => {
    const today = centralToday();
    const from = new Date(today);
    if (label === 'This Week') from.setDate(from.getDate() - (from.getDay() + 6) % 7);
    if (label === 'This Month') from.setDate(1);
    if (label === 'This Year') { from.setMonth(0, 1); setGroupBy('MONTH'); }
    setStart(from); setEnd(today);
  };

  const run = async () => {
    if (!start || !end) { setError('Enter both a From Date and a To Date.'); return; }
    if (dateParam(start) > dateParam(end)) { setError('End date must be on or after start date.'); return; }
    setLoading(true); setError(null); setResult(null);
    try { setResult(await getDispatcherStreetTurns({ start: dateParam(start), end: dateParam(end), groupby: groupBy })); }
    catch (e) { setError(reportError(e, 'Unable to load street turn counts. Please try again.')); }
    finally { setLoading(false); }
  };

  return <div className="p-4 space-y-4 text-label">
    <h1 className="text-xl font-semibold">Dispatcher Street Turn Counts</h1>
    <div className="rounded-lg border border-theme-border-primary bg-theme-bg-primary p-4 space-y-3">
      <div className="flex flex-wrap gap-2" aria-label="Date presets">
        {PRESETS.map(label => <button key={label} onClick={() => preset(label)} disabled={loading}
          className="rounded border border-theme-border-primary px-3 py-1 text-sm hover:bg-theme-bg-secondary disabled:opacity-50">{label}</button>)}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-4xl">
        <DateTimePicker mode="date" labelType="custom" label="From Date" ariaLabel="From Date" required value={start} onChange={value => setStart(calendarDate(value))} disabled={loading} />
        <DateTimePicker mode="date" labelType="custom" label="To Date" ariaLabel="To Date" required value={end} onChange={value => setEnd(calendarDate(value))} disabled={loading} />
        <div><div className="text-sm mb-1">Group By</div><SingleSelect ariaLabel="Group By" options={GROUPS} value={groupBy} onChange={value => setGroupBy(value as StreetTurnGroupBy)} disabled={loading} /></div>
      </div>
      <button onClick={run} disabled={loading} className={REPORT_BUTTON}>{loading ? 'Running…' : 'Run Report'}</button>
      <p className="text-sm text-muted">Credits the person recorded as Reused By, using Reuse Date. Each linked order pair counts once. Undone and deleted turns are excluded; SSL approval is not required. Weeks start Monday. Date presets use Central time.</p>
    </div>
    {error && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>}
    {result && <>
      <p className="text-sm font-medium">{result.criteria}</p>
      <p className="font-semibold">Grand Total: {result.grandTotal.toLocaleString()} street turns</p>
      {result.rows.length === 0 && <p role="status" className="text-sm text-muted">No street turns found for this date range.</p>}
    </>}
    {(loading || (result && result.rows.length > 0)) && <div className="h-[65vh] min-h-80">
      <TanTable<StreetTurnCountsRow> data={result?.rows ?? []} columns={columns} loading={loading}
        getRowId={row => row.username || '__unknown__'} settingsKey={`dispatcher-street-turns-${result?.groupBy ?? groupBy}`}
        cardTitle="Dispatcher Street Turn Counts" searchPlaceholder="Search dispatchers..."
        enableGrouping={false} showColumnFooter exportFooter={exportFooter} fillContainer enableColumnFilters />
    </div>}
  </div>;
}
